import React, { useMemo, useState, useRef } from 'react';

interface DonutChartProps {
    /**
     * An array of exactly 3 numeric values (or more/less, logic supports generic array).
     */
    values: [number, number, number];
    /**
     * Optional size for the chart container. Defaults to 260px.
     */
    size?: number;
}

const COLORS = [
    '#7EBD61',
    '#5DA57D',
    '#F5F5F5',
    '#DB1D1D',
];

export const DonutChartSvg: React.FC<DonutChartProps> = ({ values, size = 150 }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);

    // Configuration for geometry
    const strokeWidth = size * 0.185; // ~18.5% thickness
    const gapPixels = size * 0.05;    // ~5% gap

    const radius = (size - strokeWidth) / 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;
    const total = values.reduce((acc, val) => acc + val, 0);

    // Calculate segments with overlap prevention
    const segments = useMemo(() => {
        if (total === 0) return [];

        const minAllocatedLength = strokeWidth + gapPixels;
        const rawLengths = values.map(v => (v / total) * circumference);

        let deficit = 0;
        const adjustedLengths = [...rawLengths];
        const largeIndices: number[] = [];

        adjustedLengths.forEach((len, i) => {
            if (len < minAllocatedLength) {
                deficit += (minAllocatedLength - len);
                adjustedLengths[i] = minAllocatedLength;
            } else {
                largeIndices.push(i);
            }
        });

        if (deficit > 0 && largeIndices.length > 0) {
            const totalLargeLength = largeIndices.reduce((sum, i) => sum + rawLengths[i], 0);
            largeIndices.forEach(i => {
                const ratio = rawLengths[i] / totalLargeLength;
                adjustedLengths[i] -= deficit * ratio;
            });
        }

        let accumulatedLength = 0;

        return adjustedLengths.map((allocatedLength, index) => {
            const strokeLength = Math.max(0, allocatedLength - gapPixels - strokeWidth);
            const startOffset = accumulatedLength + (gapPixels / 2) + (strokeWidth / 2);
            const rotationDeg = (startOffset / circumference) * 360;

            accumulatedLength += allocatedLength;

            return {
                color: COLORS[index % COLORS.length],
                strokeDasharray: `${strokeLength} ${circumference}`,
                rotation: rotationDeg - 90,
                originalValue: values[index]
            };
        });
    }, [values, total, circumference, gapPixels, strokeWidth]);

    const handleMouseMove = (e: React.MouseEvent, index: number) => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            // Calculate position relative to container
            setTooltipPos({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setHoveredIndex(index);
        }
    };

    const handleMouseLeave = () => {
        setHoveredIndex(null);
    };

    return (
        <div
            ref={containerRef}
            style={{ width: size, height: size, position: 'relative' }}
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                onMouseLeave={handleMouseLeave}
                style={{ overflow: 'visible' }}
            >
                {segments.map((seg, i) => {
                    if (!seg) return null;
                    const isHovered = hoveredIndex === i;

                    return (
                        <circle
                            key={i + 'chart'}
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke={seg.originalValue === 0 ? "#f5f5f5" : seg.color}
                            strokeWidth={strokeWidth}
                            strokeLinecap="round"
                            strokeDasharray={seg.strokeDasharray}
                            strokeDashoffset={0}
                            transform={`rotate(${seg.rotation} ${center} ${center})`}
                            onMouseMove={(e) => handleMouseMove(e, i)}
                            style={{
                                transition: 'all',
                                transitionDuration: '0.3s',
                                cursor: 'pointer',
                                opacity: hoveredIndex !== null && !isHovered ? 0.4 : 1,
                                filter: isHovered ? (hoveredIndex !== 2 ? 'brightness(110%)' : 'none') : 'none',
                                transformOrigin: 'center',
                                // Slight scale effect on hover for better feedback
                                transform: isHovered
                                    ? `rotate(${seg.rotation}deg) scale(1.02)`
                                    : `rotate(${seg.rotation}deg) scale(1)`
                            }}
                        />
                    );
                })}
                {/* Optional: Center Text */}

                <text
                    x="50%"
                    y="50%"
                    dy=".3em"
                    textAnchor="middle"
                    style={{ fontWeight: 800, fontSize: 28, color: 'var(--text-color)' }}
                >
                    {isNaN(Math.round((values[0] / total) * 100)) ? '0' : (Math.round((values[0] / total) * 100))}%
                </text>
            </svg>

            {/* Floating Tooltip */}
            {/* 
         FIX: added 'pointer-events-none' to ensure the mouse 
         does not interact with the tooltip div, preventing the flicker loop.
      */}
            {hoveredIndex !== null && (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipPos.x,
                        top: tooltipPos.y,
                        transform: 'translate(15px, 15px)',
                        background: 'rgba(0, 0, 0, 0.8)',
                        padding: 7,
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: 5,
                        fontSize: 13
                    }}
                >
                    <span style={{ margin: 0, whiteSpace: 'nowrap' }}>
                        {obterLabel(hoveredIndex)}:
                    </span>
                    <span>
                        {/* {Math.round((values[hoveredIndex] / total) * 100)}% */}
                        {isNaN(Math.round((values[hoveredIndex] / total) * 100)) ? '100' : (Math.round((values[hoveredIndex] / total) * 100))}%
                    </span>
                </div>
            )}
        </div>
    );
};


function obterLabel(index: number) {
    switch (index) {
        case 0:
            return 'Concluído'
        case 1:
            return 'Em andamento'
        case 2:
            return 'Não iniciado'
        default:
            break;
    }
}